<template>
  <div class="executives-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ t('menu.executives') }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchPageData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 高管列表 -->
        <div v-else-if="executivesData.length > 0" class="executives-list">
          <!-- 循环渲染高管项，直接使用索引绑定ref -->
          <div
            v-for="(executive, index) in executivesData"
            :key="index"
            class="executive-item"
            :class="{ 'animate-fade-in': true }"
            :style="`animation-delay: ${index * 0.1}s`"
          >
            <!-- 高管头像 -->
            <div class="executive-avatar">
              <img
                v-if="executive.photo_url.length"
                :src="executive.photo_url"
                :alt="`${executive.last_name}${executive.first_name}`"
                class="avatar-img"
                @error="handleImgError($event)"
              />
              <div v-else class="avatar-placeholder" :class="executive.gender">
                <span class="placeholder-text">
                  {{executive.last_name}}
<!--                  <el-icon><User></User></el-icon>-->
<!--                  {{executive.last_name.replace(' ', '')}}-->
<!--                  {{ getFirstChar(`${executive.last_name}${executive.first_name}`) }}-->
                </span>
              </div>
            </div>

            <!-- 高管信息 -->
            <div class="executive-info">
              <div class="executive-name">
                <span class="name-text">{{ executive.last_name }}{{ executive.first_name }}</span>
                <span
                  v-if="executive.is_executive_director"
                  class="executive-tag"
                >
                  {{ t('executives.executive_directors') }}
                </span>
                <span
                  v-else
                  class="executive-tag independent"
                >
                  {{ t('executives.independent_directors') }}
                </span>
              </div>
              <div class="executive-position" v-if="executive.is_executive_director">{{ executive.position }}</div>
              <div class="executive-bio">
                <!-- 处理换行，避免v-html潜在风险 -->
                <p v-for="(paragraph, pIndex) in formatBio(executive.bio)" :key="pIndex" class="bio-paragraph">
                  {{ paragraph }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-else class="empty-state">
          <div class="empty-icon">
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M40 70C56.5685 70 70 56.5685 70 40C70 23.4315 56.5685 10 40 10C23.4315 10 10 23.4315 10 40C10 56.5685 23.4315 70 40 70Z"
                stroke="var(--el-color-primary-light-8)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M30 40L35 35L45 45M50 40L45 35L35 45"
                stroke="var(--el-color-primary-light-6)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M40 50C45 50 48 47 48 42C48 37 45 34 40 34C35 34 32 37 32 42C32 47 35 50 40 50Z"
                stroke="var(--el-color-primary-light-6)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3 class="empty-title">{{ t('common.no_data', '暂无数据') }}</h3>
          <p class="empty-description">{{ t('executives.no_executives', '暂无高管信息') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Warning } from '@element-plus/icons-vue'
import { getSinglePageContent } from "@/assets/api/website";
import { User } from '@element-plus/icons-vue';

// 使用 i18n（添加默认值避免翻译缺失）
const { locale, t } = useI18n()

// 响应式数据
const executivesData = ref([])
const isLoading = ref(false)
const errorMessage = ref('')

// 修复：替换过滤器为普通函数（Vue3 setup不支持过滤器）
const getFirstChar = (name) => {
  return name && name.length > 0 ? name.charAt(0) : '?'
}

// 修复：处理简介换行（避免v-html安全风险）
const formatBio = (bio) => {
  if (!bio) return ['']
  // 按两个换行符分割段落，过滤空段落
  return bio.split(/\n{2,}/).filter(paragraph => paragraph.trim())
}

// 处理头像加载失败
const handleImgError = (e) => {
  e.target.style.display = 'none'
  // 显示占位符（通过CSS控制，无需额外DOM操作）
  const placeholder = e.target.nextElementSibling
  if (placeholder) placeholder.style.display = 'flex'
}

// 获取页面数据（添加调试日志）
const fetchPageData = async () => {
  isLoading.value = true
  errorMessage.value = ''
  // console.log('开始获取高管数据，当前语言：', locale.value)

  try {
    const currentLocale = locale.value
    const response = await getSinglePageContent({
      lang: currentLocale,
      path: '/executives'
    })

    // console.log('接口响应：', response)
    if (response && response.content) {
      executivesData.value = response.content.list || []
      // console.log('高管数据加载成功：', executivesData.value)
    } else {
      executivesData.value = []
      // console.log('无高管数据')
    }
  } catch (error) {
    console.error('获取高管数据失败：', error)
    errorMessage.value = t('common.load_failed', '加载失败')
  } finally {
    isLoading.value = false
  }
}

// 监听语言变化，重新加载数据
watch(locale, (newLocale) => {
  // console.log('语言切换为：', newLocale)
  fetchPageData()
})

// 组件挂载时获取数据（确保DOM渲染完成）
onMounted(() => {
  // console.log('组件挂载，开始加载数据')
  fetchPageData()
})
</script>

<style scoped lang="scss">
// 导入全局样式变量
@import '@/assets/css/common';

@import '@/assets/css/business';
.executives-page {
  min-height: 100vh;
  background: linear-gradient(180deg, $gray-10 0%, $white 100%);
}

.header-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, $primary-color, $secondary-color);
  margin: 0 auto;
  border-radius: 2px;

  @media (max-width: 480px) {
    width: 60px;
    height: 3px;
  }
}

// 主要内容区域
.page-content {
  padding: 60px 0;

  @media (max-width: 768px) {
    padding: 40px 0;
  }

  @media (max-width: 480px) {
    padding: 30px 0;
  }
}

// 高管列表（确保布局正确）
.executives-list {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 30px; // 行间距

  @media (max-width: 768px) {
    gap: 24px;
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    gap: 20px;
    padding: 0 12px;
  }
}

// 单个高管项（修复动画逻辑，改为页面加载时依次淡入）
.executive-item {
  display: flex;
  align-items: flex-start;
  background: $white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);
  // 初始状态
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease, transform 0.5s ease;

  @media (max-width: 768px) {
    padding: 24px;
    flex-direction: column;
    align-items: center; // 移动端居中
  }

  @media (max-width: 480px) {
    padding: 20px 16px;
  }
}

// 修复：简化动画，页面加载时依次淡入（无需Intersection Observer）
.animate-fade-in {
  animation: fadeInUp 0.5s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 高管头像（修复占位符显示逻辑）
.executive-avatar {
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  margin-right: 30px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid $primary-light-8;
  position: relative;

  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
  }
}

.avatar-img {
  width: 100%;
  //height: 100%;
  object-fit: cover;
  display: block; // 避免默认空隙
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $primary-light-9, $primary-light-7);
  color: $primary-color-2;
  position: absolute;
  top: 0;
  left: 0;

  &.male {
    background: linear-gradient(135deg, $primary-light-9, $primary-light-7);
  }

  &.female {
    background: linear-gradient(135deg, #F8E8F0, #F0D8E0);
  }
}

.placeholder-text {
  font-size: 2rem;
  font-weight: 700;
  font-family: $font-serif;
}

// 高管信息（确保文本不溢出）
.executive-info {
  flex: 1;
  width: 100%;
  min-width: 0; // 解决flex子元素文本溢出问题
}

.executive-name {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap; // 标签换行避免溢出
}

.name-text {
  font-size: 1.5rem;
  font-weight: 700;
  color: $primary-color-2;
  font-family: $font-serif;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
}

.executive-tag {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 600;
  background: linear-gradient(135deg, $primary-light-9, $primary-light-7);
  color: $primary-color-2;
  border: 1px solid rgba($primary-color, 0.1);

  &.independent {
    background: linear-gradient(135deg, $gray-10, $primary-light-9);
    color: $gray-80;
  }
}

.executive-position {
  font-size: 1.125rem;
  color: $primary-color;
  font-weight: 600;
  margin-bottom: 16px;
  //white-space: nowrap;
  overflow: hidden;
  //text-overflow: ellipsis; // 职位过长时省略

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 12px;
    text-align: left; // 移动端居中
  }
}

.executive-bio {
  font-size: 1rem;
  line-height: 1.8;
  color: $text-color;

  @media (max-width: 768px) {
    font-size: 0.9375rem;
    line-height: 1.6;
  }
}

.bio-paragraph {
  margin: 0 0 16px 0;
  text-align: justify; // 文本两端对齐

  &:last-child {
    margin-bottom: 0;
  }
}

// 加载状态（确保居中显示）
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 20px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba($primary-color, 0.1);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1rem;
  color: $text-light;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 错误状态（确保样式正常）
.error-container {
  text-align: center;
  padding: 60px 20px;
  max-width: 600px;
  margin: 0 auto;
}

.error-icon {
  margin-bottom: 20px;
  color: $secondary-color-2;
}

.error-title {
  font-size: 1.5rem;
  color: $secondary-color-2;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.error-message {
  font-size: 1rem;
  color: $text-light;
  margin-bottom: 20px;
  line-height: 1.5;
}

.retry-button {
  background: $primary-color;
  color: $white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $primary-dark-1;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
}

// 空状态（确保居中显示）
.empty-state {
  text-align: center;
  padding: 80px 20px;
  max-width: 600px;
  margin: 0 auto;
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.7;
}

.empty-title {
  font-size: 1.5rem;
  color: $text-color;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.empty-description {
  font-size: 1rem;
  color: $text-light;
  line-height: 1.5;
}
</style>